/**
 * Memory list page — `GET /memories`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` for the title and secondary action, `CordioAbpPagedDataTable` for the grid, one
 * `DeleteDialog` driven by `useDeleteDialog`, and a `.memory-list` BEM block.
 *
 * WHY the grid owns paging, search and filtering: those used to be page state (`filters`, plus a
 * `useEffect` with nine dependencies that refetched on every keystroke), which carried none of the
 * `data-testid` attributes the E2E grid checklist selects on. `listVersion` is the one piece of
 * refetch state that survives — bumping it rebuilds `dataSource`, which is how the grid is told to
 * reload after a delete it did not perform.
 *
 * WHY the page still windows results itself: `GET /memories` is NOT a paged endpoint. It takes a
 * `limit` and returns a bare `AgentMemoryEntryDto[]`, so there is no server `totalCount` to page
 * against. {@link toClientPage} therefore slices the fetched window (capped at
 * {@link MEMORY_FETCH_LIMIT}) and reports its length as the total. `createCortexPagedDataSource`
 * still owns the ABP offset→page arithmetic; only the page→slice inverse lives here, and only
 * because the endpoint cannot do it server-side.
 *
 * Capabilities preserved outside the grid:
 *   - `?sessionId=` scoping — the Session detail page links here to show one session's memories;
 *     the id is read from the URL, passed to the service, and surfaces a "Back to session" action.
 *   - Semantic search and the cross-scope includes (`useSemanticSearch`, `includeProjectScope`,
 *     `includeTenantScope`) stay pinned on, exactly as the old page sent them.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field           | header     | width | cell type            | filter
 * ----------------|------------|-------|----------------------|---------------------------------
 * summary         | Summary    | 34%   | text, 120 + readMore | toolbar search -> `query`
 * scope           | Scope      | 12%   | text (enum label)    | select `scope` (0/1/2)
 * type            | Type       | 12%   | text (enum label)    | select `type` (0/1/2/3)
 * importanceScore | Importance | 12%   | text (2 dp)          | number `minImportanceScore`
 * tags            | Tags       | 14%   | custom (Chip x3 + n) | —
 * createdAt       | Created    | 16%   | date, locale         | —
 *
 * Nothing was dropped: the pre-migration table showed exactly these six plus an actions cell, and
 * the actions cell is now the grid's declarative `actions`.
 *
 * No status tabs. A memory entry has no active/inactive dimension; its nearest equivalent is
 * `scope`, whose values are 0/1/2 rather than true/false, so a tab strip would emit
 * `cordio-datatable-status-tab-0…2` and break the `-all` / `-true` / `-false` ids GRID-13 selects
 * on. Scope is a popover filter instead.
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { ArrowLeft, Trash2 } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { memoryService } from '../services/memoryService';
import { useNotificationStore } from '../store/notificationStore';
import { MemoryScope, MemoryType, type AgentMemoryEntryDto, type MemoryFilters } from '../types/memory';
import { ROUTES } from '../utils/routes';

import type { PagedResult } from '../types/tenant';

import './MemoryListPage.scss';

/**
 * How many entries one fetch asks `GET /memories` for. The endpoint is unpaged, so this is both
 * the retrieval budget and the ceiling on the grid's reported total. 200 is eight grid pages at
 * the default page size and a superset of the 50 the pre-migration page requested.
 */
const MEMORY_FETCH_LIMIT = 200;

/** How many tag chips fit in the Tags cell before the rest collapse into a `+n` chip. */
const MAX_VISIBLE_TAGS = 3;

const SCOPE_OPTIONS = [
  { label: 'Session', value: String(MemoryScope.Session) },
  { label: 'Project', value: String(MemoryScope.Project) },
  { label: 'Tenant', value: String(MemoryScope.Tenant) },
] as const;

const TYPE_OPTIONS = [
  { label: 'Fact', value: String(MemoryType.Fact) },
  { label: 'Decision', value: String(MemoryType.Decision) },
  { label: 'Outcome', value: String(MemoryType.Outcome) },
  { label: 'Preference', value: String(MemoryType.Preference) },
] as const;

const scopeLabel = (scope: number): string =>
  SCOPE_OPTIONS.find((o) => o.value === String(scope))?.label ?? 'Unknown';

const typeLabel = (type: number): string =>
  TYPE_OPTIONS.find((o) => o.value === String(type))?.label ?? 'Unknown';

/** Reads a toolbar filter the grid stores as a string back into a finite number. */
function asFiniteNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Turns the grid's query into the `MemoryFilters` shape `memoryService.getMemories` accepts.
 *
 * Only keys the endpoint actually reads are emitted — the service builds its query string from an
 * explicit allow-list, so anything else would be silently discarded and look like a broken filter.
 * Note the rename: the grid's free-text search arrives as `searchTerm`, but `/memories` spells it
 * `query` (it is what feeds the semantic search).
 */
function toMemoryFilters(query: CortexPagedQuery): MemoryFilters {
  const filters: MemoryFilters = {};

  if (typeof query.searchTerm === 'string' && query.searchTerm.trim() !== '') {
    filters.query = query.searchTerm;
  }

  const scope = asFiniteNumber(query.scope);
  if (scope !== undefined) filters.scope = scope as MemoryScope;

  const type = asFiniteNumber(query.type);
  if (type !== undefined) filters.type = type as MemoryType;

  const minImportanceScore = asFiniteNumber(query.minImportanceScore);
  if (minImportanceScore !== undefined) filters.minImportanceScore = minImportanceScore;

  return filters;
}

/**
 * Wraps an unpaged array response as the one page the grid asked for.
 *
 * `totalCount` is the size of the fetched window, not the size of the table on the server — the
 * endpoint does not report one. That is honest for a `limit`-capped search endpoint, and it keeps
 * the pagination summary consistent with what the user can actually reach.
 */
function toClientPage<T>(items: readonly T[], pageNumber: number, pageSize: number): PagedResult<T> {
  const start = Math.max(0, (pageNumber - 1) * pageSize);
  return {
    items: items.slice(start, start + pageSize) as T[],
    totalCount: items.length,
    pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
    hasPreviousPage: pageNumber > 1,
    hasNextPage: start + pageSize < items.length,
  };
}

export const MemoryListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotificationStore();

  /** Bumped after a delete to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<AgentMemoryEntryDto>();

  /**
   * Session scoping arrives as `?sessionId=…` when the Session detail page links here. It is not a
   * grid filter: it narrows which list this page *is*, and it drives the "Back to session" action.
   */
  const sessionId = useMemo(
    () => new URLSearchParams(location.search).get('sessionId') ?? undefined,
    [location.search],
  );

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<AgentMemoryEntryDto>(async (query) => {
        const items = await memoryService.getMemories({
          ...toMemoryFilters(query),
          sessionId,
          // Pinned on, as the pre-migration page sent them: semantic recall plus the wider scopes
          // are what make this page useful rather than a raw dump of the session's own rows.
          useSemanticSearch: true,
          includeProjectScope: true,
          includeTenantScope: true,
          limit: MEMORY_FETCH_LIMIT,
        });
        return toClientPage(items, query.pageNumber, query.pageSize);
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentMemoryEntryDto>>>(
    () => [
      {
        field: 'summary',
        header: 'Summary',
        type: 'text',
        width: '34%',
        clickable: true,
        maxLength: 120,
        showReadMore: true,
        // `summary` is optional — the pipeline only writes one when it condensed the entry, so
        // fall back to the raw content rather than showing an em dash for a row with plenty to say.
        valueGetter: (row) => row.summary || row.content,
      },
      {
        field: 'scope',
        header: 'Scope',
        type: 'text',
        width: '12%',
        valueGetter: (row) => scopeLabel(row.scope),
      },
      {
        field: 'type',
        header: 'Type',
        type: 'text',
        width: '12%',
        valueGetter: (row) => typeLabel(row.type),
      },
      {
        field: 'importanceScore',
        header: 'Importance',
        type: 'text',
        width: '12%',
        // Two decimals, as before: the raw score carries far more precision than the ranking
        // decision it feeds.
        valueGetter: (row) => row.importanceScore.toFixed(2),
      },
      {
        field: 'tags',
        header: 'Tags',
        type: 'custom',
        width: '14%',
        customCell: (row) => {
          const tags = row.tags ?? [];
          return (
            <span className="memory-list__tags">
              {tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                <Chip key={`${row.id}-${tag}`} label={tag} variant="outlined" />
              ))}
              {tags.length > MAX_VISIBLE_TAGS && (
                <Chip label={`+${tags.length - MAX_VISIBLE_TAGS}`} variant="outlined" />
              )}
            </span>
          );
        },
      },
      { field: 'createdAt', header: 'Created', type: 'date', width: '16%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search memories by meaning or text' },
      filters: [
        { key: 'scope', label: 'Scope', type: 'select', options: [...SCOPE_OPTIONS] },
        { key: 'type', label: 'Type', type: 'select', options: [...TYPE_OPTIONS] },
        {
          key: 'minImportanceScore',
          label: 'Min importance',
          type: 'number',
          placeholder: '0.00 - 1.00',
          description: 'Hides entries the retriever scored below this.',
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentMemoryEntryDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View memory',
        onClick: (row) => navigate(`${ROUTES.MEMORIES}/${row.id}`),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete memory',
        className: 'memory-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await memoryService.deleteMemory(pendingItem.id);
    addNotification('Memory deleted', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, addNotification]);

  return (
    <div className="memory-list" data-testid="memory-list-page">
      <ShellHeader
        title="Memories"
        data-testid="memory-list-header"
        description={
          sessionId
            ? "Memory entries this session can recall, including the project and tenant entries visible to it."
            : 'What the agents remembered — facts, decisions, outcomes and preferences, scored for recall.'
        }
        actions={
          sessionId ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => navigate(`${ROUTES.SESSIONS}/${sessionId}`)}
            >
              <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
              Back to session
            </Button>
          ) : undefined
        }
      />

      <div data-testid="memory-list-grid">
        <CordioAbpPagedDataTable<AgentMemoryEntryDto>
          className="memory-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.MEMORIES}/${row.id}`)}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete memory?"
        entityLabel={pendingItem?.summary || pendingItem?.content}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The agent stops recalling this entry. Whatever it already influenced stays as it is, and this cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
